// steps/DetailsStep.tsx

import React, { Fragment, useEffect, useMemo } from "react";
import { Box, Button, CircularProgress, Divider, IconButton, Stack, Typography } from "@mui/material";
import { Check, Edit3, Lock, Trash } from "lucide-react";

import { useAllowScroll } from "../AllowScrollContext";
import { FuhrparkSummary, MaterialSummary, MengeSummary, SonstigesSummary, TaktSummary, UhrzeitSummary } from "./Summaries";
import { MaterialOrder } from "@/components/planning/Project";
import { alertPolyfill } from "@/components/planning/editors/DayEditor";
import MengeStep from "./MengeStep";
import UhrzeitStep from "./UhrzeitStep";
import FuhrparkStep from "./FuhrparkStep";
import SonstigesStep from "./SonstigesStep";

export type EditorMode = "create" | "edit";
export type EditSection = "MATERIAL" | "MENGE" | "UHRZEIT" | "FUHRPARK" | "SONSTIGES" | "BAUHOF";

export function TitleWithWarning({ text, warning }: { text: string; warning?: boolean }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      {warning ? <Box sx={{ width: 8, height: 8, borderRadius: 9999, bgcolor: "red" }} /> : null}
      <Typography variant="subtitle2" sx={{ userSelect: "none" }}>
        {text}
      </Typography>
    </Stack>
  );
}

function filterKeinTransport(state: MaterialOrder): MaterialOrder {
  return {
    ...state,
    transportInformation: state.transportInformation?.filter((t) => (t.amount ?? 0) !== 0 && t.type !== "XX") ?? null,
  };
}

export default function DetailsStep({
  order,
  setOrder, // wird aktuell nicht verändert, bleibt für spätere Erweiterungen da
  mode,
  expertMode,
  jobPosition,
  isSaving,
  saveAllowed = true,
  isDispo,
  onSave,
  onSend,
  onDelete,
  jobId, // derzeit ungenutzt, bleibt in der Signatur
  onEditSection,
}: {
  order: MaterialOrder;
  jobPosition: any;
  isDispo: boolean;
  setOrder: React.Dispatch<React.SetStateAction<MaterialOrder>>;
  mode: EditorMode;
  expertMode: boolean;
  isSaving: boolean;
  saveAllowed?: boolean;
  onSave: (order: MaterialOrder) => void;
  onSend: () => void;
  onDelete?: () => void;
  jobId: number;
  onEditSection: (section: EditSection) => void;
}) {
  const { setAllowScroll } = useAllowScroll();

  useEffect(() => {
    setAllowScroll(true);
  }, []);

  const updateOrder = (patch: Partial<MaterialOrder>) => {
    const newOrder: MaterialOrder = { ...order, ...patch };
    setOrder(newOrder);
  };

  const valid = useMemo(() => {
    const ti = order.transportInformation ?? [];
    const hasSiteWork = ti.some((x) => !!x.siteWork);

    const baseValid =
      ((order.amount > 0 && !!order.article) ||
        (!order.article && order.amount === -1 && ti.reduce<boolean | null>((c, f) => c !== false && !!f.siteWork, null))) &&
      !(hasSiteWork && order.duration === "INVALID") &&
      !ti.some((x) => x.amount === 0 || x.type === "XX");

    return baseValid && saveAllowed;
  }, [order, saveAllowed]);

  const isBaustellendienst = order.amount === -1 && !order.article;
  const isBauhof = !!order.article && order.article.bauhof > 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {/* 1) Material */}
      <SectionCard expertMode={false} title="Material" onEdit={() => onEditSection(isBauhof ? "BAUHOF" : "MATERIAL")}>
        <MaterialSummary order={order} isBauhof={isBauhof} isBaustellendienst={isBaustellendienst} />
      </SectionCard>

      {/* 2) Menge – nur bei „normalem“/Deponie-Material, nicht bei Bauhof oder reinem Baustellendienst */}
      {!isBaustellendienst && !isBauhof ? (
        <SectionCard expertMode={expertMode} title="Menge" onEdit={() => onEditSection("MENGE")}>
          {expertMode ? (
            <MengeStep order={order} expertMode isDispo={isDispo} onBack={undefined} onSelect={updateOrder} />
          ) : (
            <MengeSummary isDispo={isDispo} order={order} />
          )}
        </SectionCard>
      ) : null}

      {/* 3) Uhrzeit */}
      <SectionCard expertMode={expertMode} title="Uhrzeit" onEdit={() => onEditSection("UHRZEIT")}>
        {expertMode ? (
          <UhrzeitStep jobId={jobId} jobPosition={jobPosition} order={order} expertMode isDispo={isDispo} onBack={undefined} onSelect={updateOrder} />
        ) : (
          <UhrzeitSummary order={order} />
        )}
      </SectionCard>

      {/* 4) Fuhrpark */}
      <SectionCard expertMode={expertMode} title="Fuhrpark" onEdit={() => onEditSection("FUHRPARK")}>
        {expertMode ? (
          <FuhrparkStep
            jobPosition={jobPosition}
            jobId={jobId}
            order={order}
            expertMode
            isDispo={isDispo}
            onBack={undefined}
            onSelect={updateOrder}
          />
        ) : (
          <FuhrparkSummary order={order} />
        )}
      </SectionCard>

      {/* 5) Sonstiges */}
      <SectionCard expertMode={expertMode} title="Sonstiges" onEdit={() => onEditSection("SONSTIGES")}>
        {expertMode ? (
          <SonstigesStep order={order} expertMode isDispo={isDispo} onBack={undefined} onSelect={updateOrder} />
        ) : (
          <SonstigesSummary isDispo={isDispo} expertMode={expertMode} order={order} />
        )}
      </SectionCard>

      <Box sx={{}}>
        {isSaving ? (
          <CircularProgress size={32} sx={{ alignSelf: "center" }} />
        ) : (
          <Button
            variant="contained"
            fullWidth
            disabled={!valid}
            startIcon={<Check size={18} />}
            sx={{ opacity: valid ? 1 : 0.7 }}
            onClick={() => onSave(filterKeinTransport(order))}
          >
            Speichern
          </Button>
        )}

        {true ? null : isSaving ? (
          <CircularProgress size={32} sx={{ alignSelf: "center" }} />
        ) : (
          <Button
            variant="contained"
            color="secondary"
            disabled={!valid}
            startIcon={<Lock size={18} />}
            sx={{ opacity: valid ? 1 : 0.7 }}
            onClick={() =>
              alertPolyfill("Bestellung sicher absenden?", "Die Bestellung kann danach nicht mehr geändert werden!", [
                {
                  text: "Nein, nur speichern.",
                  onPress: () => onSave(filterKeinTransport(order)),
                  style: "cancel",
                },
                {
                  text: "Ja, Bestellung absenden!",
                  onPress: () => onSend(),
                },
              ])
            }
          >
            Speichern und direkt Bestellen
          </Button>
        )}
      </Box>

      {false && mode === "edit" && onDelete ? (
        <Fragment>
          <Divider sx={{ my: 3 }} />
          {isSaving ? (
            <CircularProgress size={24} />
          ) : (
            <Button variant="contained" color="error" onClick={onDelete} startIcon={<Trash size={18} />}>
              Löschen
            </Button>
          )}
        </Fragment>
      ) : null}
    </Box>
  );
}

function SectionCard({ title, onEdit, children, expertMode }: { expertMode: boolean; title: string; onEdit: () => void; children: React.ReactNode }) {
  return (
    <Stack sx={{ mb: 1 }} spacing={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, borderBottom: 2, borderColor: "primary.main" }}>
        <Typography variant="button" sx={{ userSelect: "none", fontWeight: "bold" }}>
          {title}
        </Typography>

        {!expertMode && (
          <IconButton size="small" onClick={onEdit} aria-label={`${title} bearbeiten`}>
            <Edit3 size={18} />
          </IconButton>
        )}
      </Stack>

      {children}
    </Stack>
  );
}
